/* eslint-disable no-console */
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  TextField,
  Tooltip,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Switch,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { db } from "../firebase";
import { updateRankings } from "./Rankings";

function parseDateStr(dateStr) {
  if (!dateStr) {
    return new Date();
  }
  const parts = dateStr.split("/");
  if (parts.length < 3) {
    return new Date();
  }
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) {
    return new Date();
  }
  return new Date(year, month - 1, day);
}

function groupMatchesByPlayerPair(matches = []) {
  const grouped = {};
  matches.forEach((m) => {
    const p1 = m.team1?.name || "Player1";
    const p2 = m.team2?.name || "Player2";
    const pairKey = `${p1}##${p2}`;
    if (!grouped[pairKey]) {
      grouped[pairKey] = [];
    }
    grouped[pairKey].push(m);
  });
  return grouped;
}

function groupBySection(matchGroup) {
  const sections = {};
  matchGroup.forEach((match) => {
    const sec = match.section || "NoSection";
    if (!sections[sec]) {
      sections[sec] = [];
    }
    sections[sec].push(match);
  });
  return sections;
}

export default function IndividualsScoreSheet({ pennantId, weekData }) {
  const [pennantData, setPennantData] = useState(null);
  const [scores, setScores] = useState({});
  const [matchPoints, setMatchPoints] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pennantId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "Pennants", pennantId));
        if (snap.exists()) {
          setPennantData(snap.data());
        }
      } catch (err) {
        console.error("Error fetching Individuals pennant data:", err);
      }
    })();
  }, [pennantId]);

  useEffect(() => {
    if (!weekData?.id || !pennantId) return;
    (async () => {
      try {
        setScores({});
        setMatchPoints({});

        const matchQuery = query(
          collection(db, "Matches"),
          where("pennantId", "==", pennantId),
          where("weekId", "==", weekData.id)
        );
        const snap = await getDocs(matchQuery);

        const loadedScores = {};
        const loadedPoints = {};

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const docId = docSnap.id;
          const { team1, team2 } = data;
          if (!team1 || !team2) return;

          loadedScores[docId] = {
            [team1.name || "P1"]: team1.scores || {},
            [team2.name || "P2"]: team2.scores || {},
          };

          loadedPoints[docId] = {
            team1: team1.matchPoints || 0,
            team2: team2.matchPoints || 0,
            isManual: true,
          };
        });

        setScores(loadedScores);
        setMatchPoints(loadedPoints);
      } catch (err) {
        console.error("Error fetching Individual matches:", err);
      }
    })();
  }, [pennantId, weekData]);

  let pointsPerGame = 21;
  let gamesPerSet = 3;
  let matchFormat = "Best of 3";
  let scoringSystem = "Rally Point";
  let tieBreakerRule = "";
  let pointsCalculation = "Per Rally";

  if (pennantData?.scoringType) {
    const st = pennantData.scoringType;
    pointsPerGame = parseInt(st.pointsPerGame, 10) || 21;
    gamesPerSet = parseInt(st.gamesPerSet, 10) || 3;
    matchFormat = st.matchFormat || "Best of 3";
    tieBreakerRule = st.tieBreakerRule || "";
    scoringSystem = st.scoringSystem || "Rally Point";
    pointsCalculation = st.pointsCalculation || "Per Rally";
  }
  const setsCount = gamesPerSet;

  const getMaxMatchPoints = useCallback(() => {
    if (pointsCalculation === "Per Game" || pointsCalculation === "Per Set") {
      return setsCount;
    }
    if (pointsCalculation === "Per Rally") {
      return setsCount * pointsPerGame;
    }
    if (pointsCalculation === "Per Match") {
      return 1;
    }
    return setsCount;
  }, [pointsCalculation, setsCount, pointsPerGame]);


  const recalcMatchPoints = useCallback(
    (uniqueKey, updatedScores) => {
      const rowScores = updatedScores[uniqueKey] || {};
      const players = Object.keys(rowScores);
      if (players.length !== 2) {
        return { team1Points: 0, team2Points: 0 };
      }
      const [p1, p2] = players;
      const p1Sets = rowScores[p1];
      const p2Sets = rowScores[p2];

      let team1Points = 0;
      let team2Points = 0;

      if (pointsCalculation === "Per Game") {
        for (let i = 0; i < setsCount; i += 1) {
          const s1 = p1Sets[i] || 0;
          const s2 = p2Sets[i] || 0;

          if (tieBreakerRule.toLowerCase().includes("first to")) {
            if (s1 >= pointsPerGame && s1 > s2) {
              team1Points += 1;
            }
            if (s2 >= pointsPerGame && s2 > s1) {
              team2Points += 1;
            }
          } else if (tieBreakerRule.toLowerCase().includes("win by 2")) {
            if (s1 >= pointsPerGame && (s1 - s2) >= 2) {
              team1Points += 1;
            }
            if (s2 >= pointsPerGame && (s2 - s1) >= 2) {
              team2Points += 1;
            }
          } else {

            if (s1 >= pointsPerGame && s1 > s2) {
              team1Points += 1;
            }
            if (s2 >= pointsPerGame && s2 > s1) {
              team2Points += 1;
            }
          }
        }
      } else if (pointsCalculation === "Per Rally") {
        for (let i = 0; i < setsCount; i += 1) {
          team1Points += p1Sets[i] || 0;
          team2Points += p2Sets[i] || 0;
        }
      } else if (pointsCalculation === "Per Match") {
        let total1 = 0;
        let total2 = 0;
        for (let i = 0; i < setsCount; i += 1) {
          total1 += p1Sets[i] || 0;
          total2 += p2Sets[i] || 0;
        }
        if (total1 > total2) {
          team1Points = 1;
        } else if (total2 > total1) {
          team2Points = 1;
        }
      }
      return { team1Points, team2Points };
    },
    [pointsCalculation, setsCount, pointsPerGame, tieBreakerRule]
  );

  const handleManualToggle = useCallback(
    (uniqueKey, isManual) => {
      setMatchPoints((prev) => {
        const newObj = {
          ...(prev[uniqueKey] || {}),
          isManual,
        };
        if (!isManual) {

          const mp = recalcMatchPoints(uniqueKey, scores);
          newObj.team1 = mp.team1Points;
          newObj.team2 = mp.team2Points;
        }
        return {
          ...prev,
          [uniqueKey]: newObj,
        };
      });
    },
    [recalcMatchPoints, scores]
  );

  const handleScoreChange = useCallback(
    (uniqueKey, playerName, setIndex, rawValue) => {
      if (matchPoints[uniqueKey]?.isManual) {

        return;
      }
      setScores((prev) => {
        const copy = { ...prev };
        if (!copy[uniqueKey]) {
          copy[uniqueKey] = {};
        }
        if (!copy[uniqueKey][playerName]) {
          copy[uniqueKey][playerName] = {};
        }

        const parsed = parseInt(rawValue, 10);
        if (Number.isNaN(parsed)) {
          copy[uniqueKey][playerName][setIndex] = "";
        } else {

          copy[uniqueKey][playerName][setIndex] =
            parsed > pointsPerGame ? pointsPerGame : parsed;
        }

        const mp = recalcMatchPoints(uniqueKey, copy);
        setMatchPoints((oldMap) => ({
          ...oldMap,
          [uniqueKey]: {
            ...oldMap[uniqueKey],
            team1: mp.team1Points,
            team2: mp.team2Points,
            isManual: false,
          },
        }));
        return copy;
      });
    },
    [pointsPerGame, matchPoints, recalcMatchPoints]
  );

  const handleMatchPointChange = useCallback(
    (uniqueKey, whichSide, rawValue) => {
      let val = parseInt(rawValue, 10) || 0;
      const maxPoints = getMaxMatchPoints();
      if (val > maxPoints) {
        val = maxPoints;
      }
      setMatchPoints((prev) => ({
        ...prev,
        [uniqueKey]: {
          ...(prev[uniqueKey] || {}),
          [whichSide]: val,
        },
      }));
    },
    [getMaxMatchPoints]
  );

  const calculateTotal = useCallback(
    (uniqueKey, playerName) => {
      const row = scores[uniqueKey] || {};
      const setsObj = row[playerName] || {};
      return Object.values(setsObj).reduce(
        (acc, val) => acc + (parseInt(val, 10) || 0),
        0
      );
    },
    [scores]
  );

  const saveScores = useCallback(async () => {
    if (!weekData?.id || !Array.isArray(weekData.matches)) {
      console.error("weekData is invalid or missing matches:", weekData);
      return;
    }
    setSaving(true);
    try {

      const groupedPairs = groupMatchesByPlayerPair(weekData.matches);
      const matchDataArray = [];
      const allSaves = [];

      Object.entries(groupedPairs).forEach(([pairKey, matchGroup], pairIdx) => {
        const sectionsMap = groupBySection(matchGroup);
        Object.entries(sectionsMap).forEach(([secKey, theseMatches], sectionIdx) => {
          theseMatches.forEach((matchObj, rowIndex) => {
            const uniqueKey = `${weekData.id}_${pairKey}_${weekData.section || "NoSection"}_${pairIdx}-${sectionIdx}-${rowIndex}`;
            const docPoints = matchPoints[uniqueKey] || { team1: 0, team2: 0 };
            const rowData = scores[uniqueKey] || {};
            const p1Name = matchObj.team1?.name || "UnknownP1";
            const p2Name = matchObj.team2?.name || "UnknownP2";

            const p1Sets = rowData[p1Name] || {};
            const p2Sets = rowData[p2Name] || {};
            for (let i = 0; i < setsCount; i += 1) {
              if (!p1Sets[i]) {
                p1Sets[i] = 0;
              }
              if (!p2Sets[i]) {
                p2Sets[i] = 0;
              }
            }

            const total1 = calculateTotal(uniqueKey, p1Name);
            const total2 = calculateTotal(uniqueKey, p2Name);

            const matchDocData = {
              pennantId,
              pennantName: pennantData?.name || "UnknownPennant",
              sportType: pennantData?.sportType,
              seasonId: pennantData?.seasonName || "UnknownSeason",
              weekId: weekData.id,
              matchIndex: matchObj.matchIndex || 0,
              matchDate: Timestamp.fromDate(parseDateStr(weekData.date || "")),
              weekDateStr: weekData.date,
              section: weekData.section || null,
              team1: {
                id: matchObj.team1?.id || "p1ID-unknown",
                name: p1Name,
                playerIds: matchObj.team1?.playerIds || [],
                scores: p1Sets,
                total: total1,
                matchPoints: docPoints.team1,
              },
              team2: {
                id: matchObj.team2?.id || "p2ID-unknown",
                name: p2Name,
                playerIds: matchObj.team2?.playerIds || [],
                scores: p2Sets,
                total: total2,
                matchPoints: docPoints.team2,
              },
            };

            matchDataArray.push(matchDocData);

            const docRef = doc(db, "Matches", uniqueKey);
            allSaves.push(setDoc(docRef, matchDocData, { merge: true }));
          });
        });
      });

      await Promise.all(allSaves);

      const thePennantName = pennantData?.name || "UnknownPennant";
      await updateRankings(matchDataArray, thePennantName);

      alert("Individual scores, sections, and rankings updated successfully!");
    } catch (err) {
      console.error("Error saving Individual Scores:", err);
      alert("Error saving Individual Scores.");
    } finally {
      setSaving(false);
    }
  }, [
    weekData,
    matchPoints,
    scores,
    setsCount,
    pennantData,
    pennantId,
    calculateTotal,
  ]);

  if (!pennantData) {
    return <Typography>Loading Individuals Score Sheet...</Typography>;
  }
  if (!weekData?.matches) {
    return <Typography>No matches found.</Typography>;
  }

  const groupedPairs = groupMatchesByPlayerPair(weekData.matches);

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h4" gutterBottom>
        {pennantData.name} - {pennantData.playType || "Individuals"}
      </Typography>
      <Typography>Date: {weekData?.date || "Unknown Date"}</Typography>

      <Typography variant="h6" sx={{ mt: 2 }}>
        Scoring System: {scoringSystem} (Up to {pointsPerGame} points)
      </Typography>
      <Typography variant="subtitle1">Games Per Set: {gamesPerSet}</Typography>
      <Typography variant="subtitle1">Match Format: {matchFormat}</Typography>
      <Typography variant="subtitle1">Tie Breaker: {tieBreakerRule}</Typography>
      {weekData.section && (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Section: {weekData.section}
        </Typography>
      )}

      {Object.entries(groupedPairs).map(([pairKey, matchGroup], pairIdx) => {
        const sectionsMap = groupBySection(matchGroup);
        return (
          <Accordion key={pairKey} sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{pairKey.replace("##", " vs ")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.entries(sectionsMap).map(([secKey, theseMatches], sectionIdx) => (
                <Box key={secKey} sx={{ mb: 3 }}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          {Array.from({ length: setsCount }, (_, i) => i + 1).map((n) => (
                            <TableCell align="center" key={n}>
                              Set {n}
                            </TableCell>
                          ))}
                          <TableCell align="center">Total</TableCell>
                          <TableCell align="center">Match Points</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {theseMatches.map((matchObj, rowIndex) => {
                          const uniqueKey = `${weekData.id}_${pairKey}_${weekData.section || "NoSection"}_${pairIdx}-${sectionIdx}-${rowIndex}`;
                          const p1Name = matchObj.team1?.name || "UnknownP1";
                          const p2Name = matchObj.team2?.name || "UnknownP2";

                          return (
                            <React.Fragment key={uniqueKey}>

                              <TableRow>
                                <TableCell colSpan={setsCount + 3}>
                                  <Box display="flex" alignItems="center">
                                    <Switch
                                      checked={matchPoints[uniqueKey]?.isManual ?? true}
                                      onChange={(e) =>
                                        handleManualToggle(uniqueKey, e.target.checked)
                                      }
                                      color="primary"
                                    />
                                    <Typography variant="body2">
                                      {matchPoints[uniqueKey]?.isManual ?? true
                                        ? "Manual Match Points"
                                        : "Auto-calculate Match Points"}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell>{p1Name}</TableCell>
                                {Array.from({ length: setsCount }, (_, sIdx) => sIdx).map(
                                  (sIdx) => (
                                    <TableCell align="center" key={sIdx}>
                                      <Tooltip title={`Score for ${p1Name}`}>
                                        <TextField
                                          type="number"
                                          size="small"
                                          variant="outlined"
                                          sx={{ width: 60 }}
                                          placeholder="0"
                                          value={scores[uniqueKey]?.[p1Name]?.[sIdx] ?? ""}
                                          onChange={(e) =>
                                            handleScoreChange(uniqueKey, p1Name, sIdx, e.target.value)
                                          }
                                          disabled={matchPoints[uniqueKey]?.isManual ?? true}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  )
                                )}
                                <TableCell align="center">
                                  <TextField
                                    disabled
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: 60 }}
                                    value={calculateTotal(uniqueKey, p1Name)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <TextField
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: 60 }}
                                    value={matchPoints[uniqueKey]?.team1 || 0}
                                    onChange={(e) =>
                                      handleMatchPointChange(uniqueKey, "team1", e.target.value)
                                    }
                                    disabled={!((matchPoints[uniqueKey]?.isManual) ?? true)}
                                  />
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell>{p2Name}</TableCell>
                                {Array.from({ length: setsCount }, (_, sIdx) => sIdx).map(
                                  (sIdx) => (
                                    <TableCell align="center" key={sIdx}>
                                      <Tooltip title={`Score for ${p2Name}`}>
                                        <TextField
                                          type="number"
                                          size="small"
                                          variant="outlined"
                                          sx={{ width: 60 }}
                                          placeholder="0"
                                          value={scores[uniqueKey]?.[p2Name]?.[sIdx] ?? ""}
                                          onChange={(e) =>
                                            handleScoreChange(uniqueKey, p2Name, sIdx, e.target.value)
                                          }
                                          disabled={matchPoints[uniqueKey]?.isManual ?? true}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  )
                                )}
                                <TableCell align="center">
                                  <TextField
                                    disabled
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: 60 }}
                                    value={calculateTotal(uniqueKey, p2Name)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <TextField
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: 60 }}
                                    value={matchPoints[uniqueKey]?.team2 || 0}
                                    onChange={(e) =>
                                      handleMatchPointChange(uniqueKey, "team2", e.target.value)
                                    }
                                    disabled={!((matchPoints[uniqueKey]?.isManual) ?? true)}
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell colSpan={setsCount + 3}>
                                  <hr style={{ borderTop: "1px solid #ccc" }} />
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Button
          variant="contained"
          onClick={saveScores}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Scores"}
        </Button>
      </Box>
    </Box>
  );
}

IndividualsScoreSheet.propTypes = {
  pennantId: PropTypes.string.isRequired,
  weekData: PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.string,
    section: PropTypes.string,
    matches: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};
